<template>
  <div class="tree-menu-wrapper">
    <q-item
        v-if="menuItem.mode === 'module'"
        @click="pushTo(menuItem)"
        clickable
        v-ripple
        exact
        active-class="menu-item-active"
        class="menu-item q-mx-sm q-my-xs"
        :style="{ paddingLeft: `${depth * 16 + 12}px` }"
    >
      <q-item-section avatar v-if="menuItem.icon">
        <q-icon :name="menuItem.icon" size="20px" class="menu-icon" />
      </q-item-section>

      <q-item-section>
        <q-item-label :data-i18n="menuItem.code">
          {{ $t(menuItem.code) }}
          <q-tooltip v-if="menuItem.description" :data-i18n="menuItem.description">
            {{ $t(menuItem.description) }}
          </q-tooltip>
        </q-item-label>
      </q-item-section>
    </q-item>

    <q-expansion-item
        v-else
        v-model="dataExpanded"
        :icon="menuItem.icon"
        :header-inset-level="0"
        :header-style="{ paddingLeft: `${depth * 16 + 12}px` }"
        class="menu-expansion-group"
        header-class="menu-header"
        expand-icon-class="text-grey-6"
    >
      <template v-slot:header>
        <q-item-section avatar v-if="menuItem.icon">
          <q-icon :name="menuItem.icon" size="20px" />
        </q-item-section>
        <q-item-section :data-i18n="menuItem.code">
          {{ $t(menuItem.code) }}
        </q-item-section>
      </template>
      <template v-for="(node, idx) in nodes" :key="idx">
        <tree-menu
            v-if="node.nodes && node.nodes.length > 0 && node.mode !=='panel'"
            :menu-item="node"
            :depth="depth + 1"
            @item-clicked="$emit('item-clicked', $event)"
        />

        <q-item
            v-else
            @click="pushTo(node)"
            clickable
            v-ripple
            exact
            active-class="menu-item-active"
            class="menu-item q-mx-sm q-my-xs"
            :class="node.mode==='panel'? 'text-bold' : ''"
            :style="{ paddingLeft: `${(depth + 1) * 16 + 12}px` }"
        >
          <q-item-section avatar v-if="node.icon">
            <q-icon :name="node.icon" size="20px" class="menu-icon" />
          </q-item-section>

          <q-item-section>
            <q-item-label :data-i18n="node.code">
              {{ $t(node.code) }}
            </q-item-label>
          </q-item-section>
          <q-item-section side v-if="node.mode==='panel'">
            <q-icon name="chevron_right" size="20px"/>
          </q-item-section>
        </q-item>
      </template>
    </q-expansion-item>
  </div>
</template>
<script>
  import {defineAsyncComponent} from 'vue';

  export default {
    name: "TreeMenu",

    // Référence circulaire pour la récursion
    components: {
      TreeMenu: defineAsyncComponent(() => import('./TreeMenu.vue'))
    },

    props: {
      menuItem: {
        type: Object,
        required: true,
        default: () => ({
          code: '',
          nodes: [],
          mode: 'folder',
          icon: 'folder'
        })
      },
      depth: {
        type: Number,
        default: 0
      }
    },

    emits: ['item-clicked'],

    data() {
      return {
        dataExpanded: false
      };
    },

    computed: {
      nodes() {
        return this.menuItem.nodes || [];
      }
    },

    methods: {
      pushTo(item) {
        // Émettre l'événement vers le parent (mainLayout), qui gère la navigation
        // ET la fermeture du tiroir sur viewport mobile ($q.screen.lt.md).
        // NB : ne PAS fermer le tiroir ici. L'ancien `toggleLeftSideState()` basé
        // sur isMobile (détection device) entrait en conflit avec la fermeture du
        // parent : sur un vrai mobile, le parent fermait puis ce toggle ré-ouvrait.
        this.$emit('item-clicked', item);
      }
    },

    mounted() {
      // Optionnel : Auto-expansion si un enfant est actif au chargement
      // if (this.nodes.some(node => this.$route.path.includes(node.path))) {
      //   this.dataExpanded = true;
      // }
    }
  }
</script>

<style lang="scss" scoped>
  .tree-menu-wrapper {
    width: 100%;
  }

  // Style global des items
  .menu-item {
    border-radius: 8px;
    min-height: 40px;
    // Texte neutre lisible : la primaire de marque (var(--q-app)) est trop sombre
    // pour les marques foncées (rms navy) et quasi illisible en dark. L'accent de
    // marque reste porté par l'icône (clair) et l'état actif.
    color: #3c4043;
    transition: all 0.2s ease-in-out;

    .q-item__section--avatar {
      min-width: 40px;
    }

    .menu-icon {
      color: var(--q-app);
      opacity: 1.0;
    }

    .menu-label {
      font-size: 1rem;
      font-weight: 500;
    }

    // État de survol
    &:hover:not(.menu-item-active) {
      background: rgba(0, 0, 0, 0.03);
      color: #202124;
    }

    // État ACTIF (Route actuelle)
    &.menu-item-active {
      background: rgba(var(--q-primary), 0.08) !important;
      color: var(--q-primary);
      font-weight: 600;

      .menu-icon {
        color: var(--q-primary);
        opacity: 1;
      }

      // Petite bordure décorative à gauche
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 6px;
        bottom: 6px;
        width: 4px;
        background: var(--q-primary);
        border-radius: 0 4px 4px 0;
      }
    }
  }

  // Style pour les dossiers (Expansion)
  .menu-expansion-group {
    ::v-deep(.menu-header) {
      border-radius: 8px;
      margin: 2px 8px;
      min-height: 40px;
      font-weight: 600;
      color: #3c4043;

      &:hover {
        background: rgba(0, 0, 0, 0.03);
      }

      .q-item__section--avatar {
        min-width: 40px;
      }
    }
  }

  // Ajustement typographique pour les légendes
  .q-item__label--caption {
    font-size: 0.7rem;
    letter-spacing: 0.01em;
    margin-top: 2px;
  }

  // ── Dark mode : le hover clair (rgba noir + texte #202124) est illisible sur
  //    fond sombre → overlay clair + texte clair.
  .body--dark {
    // Par défaut en dark : label + icône clairs (la primaire navy serait invisible).
    .menu-item {
      color: #cfd3da;
      .menu-icon { color: #cfd3da; }
    }

    .menu-item:hover:not(.menu-item-active) {
      background: rgba(255, 255, 255, 0.08);
      color: #e8eaed;

      .menu-icon { color: #e8eaed; }
    }

    .menu-expansion-group ::v-deep(.menu-header) { color: #cfd3da; }
    .menu-expansion-group ::v-deep(.menu-header):hover {
      background: rgba(255, 255, 255, 0.08);
    }
  }
</style>
